import React, { useState, useEffect, useCallback, useRef } from 'react'
import NewProductModal from './ProductsModal/NewProductModal'
import ProductPreviewModal from './ProductsPreview/ProductPreviewModal'

import ProductsFilterModal, { FilterStatus } from './filterproduct';

import {
  PageContainer,
  Header,
  Title,
  ButtonGroup,
  FilterButton,
  NewProductButton,
  TableHeaderContainer,
  TableWrapper,
  Table,
  TableHead,
  TableRow,
  FooterRow,
  TableCell,
  Actions,
  ActionButton,
} from './style'
import LoadMoreButton from '../../../components/LoadMoreButton'


import SearchIcon from '../../../assets/produtcss/search-in.svg'
import FilterIcon from '../../../assets/produtcss/filter.svg'
import SwitchOnIcon from '../../../assets/produtcss/Switch.svg'
import SwitchOffIcon from '../../../assets/produtcss/Switch not.svg'
import plusOn from '../../../assets/produtcss/Plus.svg'
import axios from 'axios'

type ProductPayloadType = {
  id?: number;
  name: string;
  price: number;
  size: (string | number)[];
  status: boolean;
  collection: string;
  family: string;
  groupName: string;
  colors: string[];
  pipefyId: string
};

type ProductType = {
  id: number;
  pipefyId: string;
  code: string;
  name: string;
  collection: string;
  family: string;
  type: string;
  colors: string;
  graduation: string;
  price?: string;
  status?: boolean;
  groupId?: number | null;
  group?: {
    id: number;
    name?: string;
    familyId?: number | null;
    family?: {
      id: number;
      name: string;
      colors?: { id: number; name: string; hexCode?: string }[];
    };
  };
};

const formatPrice = (price: number | string): string => {
  const numericPrice = typeof price === 'string' ? parseFloat(price) : price;

  if (isNaN(numericPrice)) {
    return 'R$ 0,00';
  }

  return numericPrice.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  });
};

const parsePrice = (priceString: string): number => {
  if (!priceString) return 0;

  const cleaned = priceString
    .replace(/[^\d,.]/g, '')
    .replace(/\./g, '')
    .replace(',', '.')

  const numericPrice = parseFloat(cleaned);
  return isNaN(numericPrice) ? 0 : numericPrice;
};

const ProductsPage = () => {
  const [allProducts, setAllProducts] = useState<ProductType[]>([])
  const [visibleProducts, setVisibleProducts] = useState<ProductType[]>([])

  const [checkedProducts, setCheckedProducts] = useState<{
    [key: number]: boolean
  }>({})
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [productToEdit, setProductToEdit] = useState<any | null>(null)
  const [previewProduct, setPreviewProduct] = useState<ProductType | null>(null)
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)

  const [page, setPage] = useState(0)
  const [pageSize] = useState(10)
  const [hasMore, setHasMore] = useState(true)

  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false)
  const [currentProductFilterStatus, setCurrentProductFilterStatus] =
    useState<FilterStatus>('all')

  const filterButtonRef = useRef<HTMLButtonElement>(null)
  const [filterButtonPosition, setFilterButtonPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  })

  useEffect(() => {
    if (isFilterModalOpen && filterButtonRef.current) {
      const rect = filterButtonRef.current.getBoundingClientRect()
      setFilterButtonPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      })
    }
  }, [isFilterModalOpen]);

  const extractUniqueColorsFromBackend = (productBackend: any): string[] => {

    let colorsFromBackend: string[] = [];

    if (productBackend.colors && Array.isArray(productBackend.colors)) {
      colorsFromBackend = productBackend.colors.map((color: any) =>
        typeof color === 'string' ? color : (color.name || String(color))
      );
    }

    else if (productBackend.group?.family?.colors && Array.isArray(productBackend.group.family.colors)) {
      colorsFromBackend = productBackend.group.family.colors.map((color: any) =>
        typeof color === 'string' ? color : (color.name || String(color))
      );
    }

    const uniqueColors = Array.from(new Set(
      colorsFromBackend
        .filter(color => color && typeof color === 'string' && color.trim() !== '')
        .map(color => color.trim())
    ));

    return uniqueColors;
  };

  const formatProductData = (productBackend: any): ProductType => {
    const collectionName = productBackend.group?.family?.collection?.name || 'N/A';
    const familyName = productBackend.group?.family?.name || 'N/A';
    const groupName = productBackend.group?.name || 'N/A';

    const uniqueColors = extractUniqueColorsFromBackend(productBackend);
    const formattedColors = uniqueColors.length > 0 ? uniqueColors.join('; ') : 'N/A';

    const formattedGraduation = Array.isArray(productBackend.size)
      ? productBackend.size.join(', ')
      : 'N/A';

    const formattedPrice = formatPrice(productBackend.price || 0);

    return {
      id: productBackend.id,
      pipefyId: productBackend.pipefyId,
      code: String(productBackend.id),
      name: productBackend.name,
      collection: collectionName,
      family: familyName,
      type: groupName,
      colors: formattedColors,
      graduation: formattedGraduation,
      price: formattedPrice,
      status: productBackend.status !== undefined ? productBackend.status : true,
      groupId: productBackend.groupId,
      group: productBackend.group,
    };
  };

  const fetchProducts = useCallback(
    async (
      currentPage: number,
      currentSize: number,
      statusFilter: FilterStatus,
    ) => {
      try {
        let url = `${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders/fashion-product/paginated?page=${currentPage}&size=${currentSize}`

        if (statusFilter === 'active') {
          url += '&status=true'
        } else if (statusFilter === 'inactive') {
          url += '&status=false'
        }

        const response = await fetch(url)

        if (!response.ok) {
          throw new Error(`Erro HTTP! Status: ${response.status}`)
        }

        const rawData = await response.json();
        const productsFromBackend = rawData.content;

        const formattedProducts: ProductType[] = productsFromBackend.map(formatProductData);

        if (currentPage === 0) {
          setAllProducts(formattedProducts)
        } else {
          setAllProducts((prev) => [...prev, ...formattedProducts])
        }

        setHasMore(!rawData.last)

        const initialChecked = formattedProducts.reduce((acc, product) => {
          if (product.id !== undefined) {
            acc[product.id] = product.status || false
          }
          return acc
        }, {} as { [key: number]: boolean })
        setCheckedProducts((prev) => ({ ...prev, ...initialChecked }))
      } catch (error) {
        console.error('Erro ao buscar produtos:', error)
        setHasMore(false)
      }
    },
    [],
  )

  useEffect(() => {
    setAllProducts([]);
    setHasMore(true);
    setPage(0);
    fetchProducts(0, pageSize, currentProductFilterStatus);
  }, [currentProductFilterStatus, pageSize, fetchProducts]);

  useEffect(() => {
    if (page > 0 && hasMore && currentProductFilterStatus === 'all') {
      fetchProducts(page, pageSize, currentProductFilterStatus)
    }
  }, [page, fetchProducts, hasMore, currentProductFilterStatus, pageSize])

  useEffect(() => {
    let filtered = allProducts
    if (currentProductFilterStatus === 'active') {
      filtered = allProducts.filter((product) => product.status === true)
    } else if (currentProductFilterStatus === 'inactive') {
      filtered = allProducts.filter((product) => product.status === false)
    }
    setVisibleProducts(filtered);
  }, [allProducts, currentProductFilterStatus]);

  const handleLoadMore = () => {
    if (hasMore && currentProductFilterStatus === 'all') {
      setPage((prevPage) => prevPage + 1)
    }
  }

  const updateProductStatus = async (
    productId: number,
    newStatus: boolean,
    pipefyId?: string,
    productData?: ProductType,
  ) => {
    try {
      const url = `${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders/fashion-product/${productId}`

      const payload = {
        status: newStatus,
        pipefyId: pipefyId,
        productData: productData,
      }

      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(
          errorData.message ||
          `Erro ao atualizar status do produto ${productId}! Status: ${response.status}`,
        )
      }

      setPage(0)
      setAllProducts([])
      setHasMore(true)
      fetchProducts(0, pageSize, currentProductFilterStatus)
    } catch (error: any) {
      console.error('Erro ao atualizar status do produto:', error.message)
      alert(`Erro ao atualizar status do produto: ${error.message}`)
    }
  }

  const statusCheck = async (productId: number) => {
    // 1. Encontre o produto no estado local para obter todos os dados, incluindo o pipefyId
    const productToUpdate = allProducts.find((p) => p.id === productId)

    if (!productToUpdate) {
      console.error('Erro: Produto não encontrado no estado local.')
      return
    }

    const currentStatus = checkedProducts[productId]
    const newStatus = !currentStatus

    // Atualiza o estado local imediatamente para feedback visual
    setCheckedProducts((prev) => ({
      ...prev,
      [productId]: newStatus,
    }))

    // 2. Chame a função de atualização com os dados necessários para o backend
    await updateProductStatus(
      productId,
      newStatus,
      productToUpdate.pipefyId,
      productToUpdate,
    )
  }

  const handleEdit = (product: ProductType) => {
    let colorsArray: string[] = [];
    if (product.colors && product.colors !== 'N/A') {
      const rawColors = product.colors.includes(';')
        ? product.colors.split(';')
        : product.colors.split(',');

      colorsArray = Array.from(new Set(
        rawColors
          .map(c => c.trim())
          .filter(c => c !== '' && c !== 'N/A')
      ));
    }

    let gradValuesArray: (string | number)[] = [];
    if (product.graduation && product.graduation !== 'N/A') {
      gradValuesArray = product.graduation
        .split(',')
        .map((v) => {
          const trimmed = v.trim();
          return isNaN(Number(trimmed)) ? trimmed : Number(trimmed);
        })
        .filter(Boolean);
    }

    const formattedPriceForModal = product.price || ''

    setProductToEdit({
      id: product.id,
      code: product.code,
      name: product.name,
      collection: product.collection,
      family: product.family,
      type: product.type,
      colors: colorsArray,
      gradValues: gradValuesArray,
      pricing: formattedPriceForModal,
      status: product.status,
    })
    setIsModalOpen(true)
  }

  const handleSaveProduct = async (productData: {
    id?: number;
    name: string;
    collection: string;
    family: string;
    type: string; // <--- CUIDADO: Espera 'type', não 'groupName'
    colors: string[];
    gradValues: (string | number)[];
    pricing: string;
    status?: boolean;
    pipefyId: string;
  }) => {
    try {
      const rawPrice = parsePrice(productData.pricing);

      const cleanedColors = productData.colors
        .filter(color => color && typeof color === 'string')
        .map(color => color.trim())
        .filter(color => color !== '' && color !== 'N/A')
        .filter(color => color.length > 0);

      const uniqueColors = Array.from(new Set(cleanedColors));

      const finalColors = uniqueColors.length > 0 ? uniqueColors : [];

      // Lógica para atualização (PUT)
      if (productData.id) {
        const existingProduct = allProducts.find(p => p.id === productData.id);

        if (!existingProduct) {
          console.error('Product not found in state. Cannot update.');
          alert('Product not found in state. Cannot update.');
          return;
        }

        const payloadOrdeno: any = {
          id: productData.id,
          name: productData.name,
          family: productData.family,
          type: productData.type,
          /* colors: productData.colors, */
          size: productData.gradValues,
          /* status: productData.id
            ? allProducts.find(p => p.id === productData.id)?.status || true
            : true, */
          collection: productData.collection,
          /* family: productData.family, */
          groupName: productData.type,
          colors: finalColors,
          price: rawPrice,
          status: productData.status,
          pipefyId: existingProduct.pipefyId
        };

        /* if (productData.id) {
          payload.id = productData.id;
        } */

        const urlOrdeno = `${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders/fashion-product/${productData.id}`;
        await axios.put(urlOrdeno, payloadOrdeno, {
          headers: { 'Content-Type': 'application/json' },
        });
      } else {
        // 1️⃣ Preparar payload para Pipefy
        const payloadPipefy = {
          name: productData.name,
          referencia: "",
          colors: productData.colors,
          groupName: productData.type,
          material: "",
          colecao: "",
          family: productData.family,
          size: productData.gradValues,
          price: rawPrice,
        };

        // 2️⃣ POST para Pipefy
        const pipefyUrl = `${process.env.REACT_APP_INTEGRATION_PIPEFY_URL}/pipefy-webhook/ordeno-product-created`;
        const responsePipefy = await axios.post(pipefyUrl, payloadPipefy, {
          headers: {
            'Content-Type': 'application/json',
          },
        });


        /* const response = await fetch(url, {
          method: method,
          headers: {
            'Content-Type': 'application/json',
            
          },
        }); */

        const pipefyObj = responsePipefy.data;
        const pipefyId = pipefyObj.details.id; // ID gerado no Pipefy

        // 3️⃣ Preparar payload para Ordeno (API local) com pipefyId
        const payloadOrdeno: any = {
          name: productData.name,
          family: productData.family,
          type: productData.type,
          colors: productData.colors,
          size: productData.gradValues,
          price: rawPrice,
          pipefyId, // ID do Pipefy
          status: true,
        };

        // 4️⃣ POST para Ordeno
        const url = `${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders/fashion-product`;
        const responseOrdeno = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payloadOrdeno),
        });

        if (!responseOrdeno.ok) {
          throw new Error('Erro ao criar produto!');
        }
      }

      // Lógica de refetch e UI (comum a ambos)
      setPage(0);
      setAllProducts([]);
      setHasMore(true);
      fetchProducts(0, pageSize, currentProductFilterStatus);
      handleCloseModal();

    } catch (error: any) {
      const msg = error?.response?.data?.message || error.message || 'Erro desconhecido';
      console.error('Erro ao salvar produto:', msg);
      alert(`Erro ao salvar produto: ${msg}`);
    }
  };

  const handleDelete = (productId: number) => {
    setAllProducts((prev) => prev.filter((product) => product.id !== productId))
  }

  const handlePreviewProduct = (product: ProductType) => {
    setPreviewProduct(product)
    setIsPreviewOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setProductToEdit(null)
  }

  const handleOpenEditModalFromPreview = (product: ProductType | any) => {
    setIsPreviewOpen(false)
    setPreviewProduct(null)
    handleEdit(product)
  }

  const handleApplyFilter = (status: FilterStatus) => {
    setCurrentProductFilterStatus(status)
    setIsFilterModalOpen(false)
  }

  const handleOpenFilterModal = () => {
    setIsFilterModalOpen(true)
  }

  return (
    <PageContainer>
      {' '}
      <Header>
        <Title>Produtos</Title>
        <ButtonGroup>
          <FilterButton ref={filterButtonRef} onClick={handleOpenFilterModal}>
            <img src={FilterIcon} alt='Filtrar' width={14} height={14} />
            Filtrar
          </FilterButton>

          <NewProductButton
            onClick={() => {
              setProductToEdit(null)
              setIsModalOpen(true)
            }}
          >
            <img src={plusOn} alt='Novo produto' width={18} height={18} />
            Novo produto
          </NewProductButton>
        </ButtonGroup>
      </Header>
      <TableHeaderContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell></TableCell>
              <TableCell>Código</TableCell>
              <TableCell>Produto</TableCell>
              <TableCell>Coleção</TableCell>
              <TableCell>Família</TableCell>
              <TableCell>Grupo</TableCell>
              <TableCell>Cores</TableCell>
              <TableCell>Graduação</TableCell>
              <TableCell>Preço</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
        </Table>
      </TableHeaderContainer>
      <TableWrapper>
        <Table>
          <tbody>
            {visibleProducts.length > 0 ? (
              visibleProducts.map((product) => (
                <TableRow key={product.id}>
                  <TableCell>
                    <button
                      onClick={() => statusCheck(product.id)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 0,
                      }}
                    >
                      <img
                        src={
                          checkedProducts[product.id]
                            ? SwitchOnIcon
                            : SwitchOffIcon
                        }
                        alt='Check'
                        width={52}
                        height={32}
                      />
                    </button>
                  </TableCell>
                  <TableCell style={{ color: '#202020' }}>
                    {product.code}
                  </TableCell>
                  <TableCell>{product.name}</TableCell>
                  <TableCell>{product.collection}</TableCell>
                  <TableCell>{product.family}</TableCell>
                  <TableCell>{product.type}</TableCell>
                  <TableCell>{product.colors}</TableCell>
                  <TableCell>{product.graduation}</TableCell>
                  <TableCell>{product.price}</TableCell>
                  <TableCell>
                    <Actions>
                      <ActionButton
                        onClick={() => handlePreviewProduct(product)}
                      >
                        <img
                          src={SearchIcon}
                          alt='Visualizar'
                          width={24}
                          height={24}
                        />
                      </ActionButton>
                    </Actions>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={10} style={{ textAlign: 'center', padding: '20px' }}>
                  Nenhum produto encontrado com o filtro atual.
                </TableCell>
              </TableRow>
            )}
          </tbody>
        </Table>
      </TableWrapper>
      {hasMore && currentProductFilterStatus === 'all' && (
        <Table>
          <tfoot>
            <FooterRow>
              <TableCell colSpan={10} style={{ textAlign: 'center', background: 'transparent' }}>
                <LoadMoreButton
                  onLoadMore={handleLoadMore}
                  currentCount={visibleProducts.length}
                  totalCount={allProducts.length}
                  hasMore={hasMore}
                />
              </TableCell>
            </FooterRow>
          </tfoot>
        </Table>
      )}
      <NewProductModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        productToEdit={productToEdit}
        onSave={handleSaveProduct}
      />
      <ProductPreviewModal
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        product={previewProduct}
        onEdit={handleOpenEditModalFromPreview}
      />
      <ProductsFilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        onApplyFilter={handleApplyFilter}
        currentFilterStatus={currentProductFilterStatus}
        top={filterButtonPosition.top}
        left={filterButtonPosition.left}
        width={filterButtonPosition.width}
      />
    </PageContainer>
  )
}

export default ProductsPage
